import { queryOptions, useMutation } from '@tanstack/react-query';
import { t } from 'i18next';
import {
  type CreateServiceAccountData,
  type CreateServiceAccountResponse,
  createServiceAccount,
  type GetApiKeysResponse,
  type GetServiceAccountsResponse,
  getApiKeys,
  getServiceAccounts,
  type RevokeApiKeyData,
  type RevokeApiKeyResponse,
  revokeApiKey,
} from 'sdk';
import { appConfig } from 'shared';
import type { ApiError } from '~/lib/api';
import { withStepUp } from '~/modules/auth/step-up';
import { StepUpDismissedError } from '~/modules/auth/step-up-retry';
import { toaster } from '~/modules/common/toaster/toaster';
import { queryClient } from '~/query/query-client';
import type { MutationData, QueryOrgContext } from '~/query/types';

const serviceAccountKeys = {
  list: (path: QueryOrgContext) => ['service-accounts', 'list', path.tenantId, path.organizationId] as const,
  apiKeys: (path: QueryOrgContext, accountId: string) => ['service-accounts', 'apiKeys', path.tenantId, path.organizationId, accountId] as const,
  create: ['service-accounts', 'create'] as const,
  revoke: ['service-accounts', 'revoke'] as const,
};

/** Id of this module's API keys tool, which is its section on the settings page: where a step-up comes back to. */
const apiKeysSection = 'api-keys';

export const serviceAccountsQueryOptions = (path: QueryOrgContext) =>
  queryOptions({
    queryKey: serviceAccountKeys.list(path),
    queryFn: () => getServiceAccounts({ path, query: { limit: appConfig.requestLimits.default } }),
  });

export const apiKeysQueryOptions = (path: QueryOrgContext, accountId: string) =>
  queryOptions({
    queryKey: serviceAccountKeys.apiKeys(path, accountId),
    queryFn: () => getApiKeys({ path: { ...path, id: accountId } }),
  });

/** One-step "create API key": the account and its first key come back together; the secret is in the response once. */
export const useCreateServiceAccountMutation = () => {
  return useMutation<CreateServiceAccountResponse, ApiError, MutationData<CreateServiceAccountData>>({
    mutationKey: serviceAccountKeys.create,
    mutationFn: ({ path, body }) => withStepUp(() => createServiceAccount({ path, body }), apiKeysSection),
    onSuccess: ({ serviceAccount, apiKey }, { path }) => {
      queryClient.setQueryData<GetServiceAccountsResponse>(serviceAccountKeys.list(path), (oldData) => {
        if (!oldData) return oldData;
        return { ...oldData, items: [serviceAccount, ...oldData.items], total: oldData.total + 1 };
      });
      // The secret never enters the cache; the listed row is the safe shape.
      if (apiKey) {
        const { secret: _secret, ...listed } = apiKey;
        queryClient.setQueryData<GetApiKeysResponse>(serviceAccountKeys.apiKeys(path, serviceAccount.id), { items: [listed] });
      }
      toaster.success(t('c:success.create_resource', { resource: t('c:api_key') }));
    },
    onError(error) {
      if (error instanceof StepUpDismissedError) return;
      console.error('Error creating API key:', error);
    },
  });
};

export const useRevokeApiKeyMutation = () => {
  return useMutation<RevokeApiKeyResponse, ApiError, MutationData<RevokeApiKeyData>>({
    mutationKey: serviceAccountKeys.revoke,
    mutationFn: ({ path }) => revokeApiKey({ path }),
    onSuccess: (revoked, { path }) => {
      queryClient.setQueryData<GetApiKeysResponse>(serviceAccountKeys.apiKeys(path, path.id), (oldData) => {
        if (!oldData) return oldData;
        return { ...oldData, items: oldData.items.map((item) => (item.id === revoked.id ? revoked : item)) };
      });
      toaster.success(t('c:success.revoke_resource', { resource: t('c:api_key') }));
    },
    onError(error) {
      console.error('Error revoking API key:', error);
    },
  });
};
